import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { getAdminSession } from '../../utils/adminSession';
import { addPlayerUrl } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const FileInput = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const UploadError = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const toTitleCase = (value: string): string => {
  if (!value) return value;

  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Preserve expected casing for values like `Forward/Defense` (used elsewhere for filtering).
const normalizePosition = (value: string): string => {
  if (!value) return value;
  return value
    .trim()
    .split('/')
    .map((part) => {
      const p = part.trim();
      if (!p) return p;
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join('/');
};

const AddPlayer: React.FC = () => {
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [number, setNumber] = useState('');
  const [shoots, setShoots] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorUrl, setSponsorUrl] = useState('');
  const [sponsorImageUrl, setSponsorImageUrl] = useState('');
  const [funFact, setFunFact] = useState('');
  const [playerImageBase64, setPlayerImageBase64] = useState<string | null>(null);
  const [playerImageFileName, setPlayerImageFileName] = useState<string | null>(null);
  const [isReadingPlayerImage, setIsReadingPlayerImage] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);

  const session = getAdminSession();
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handlePlayerImageFile = (file: File | null) => {
    setErrorMessage(null);

    if (!file) {
      setPlayerImageBase64(null);
      setPlayerImageFileName(null);
      setIsReadingPlayerImage(false);
      return;
    }

    const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPng) {
      setErrorMessage('Player image must be a PNG file.');
      return;
    }

    const maxBytes = 400 * 1024;
    if (file.size > maxBytes) {
      setErrorMessage('Player image must be 400KB or smaller.');
      return;
    }

    const reader = new FileReader();
    setIsReadingPlayerImage(true);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      // data:[mime];base64,XXXXX -> take only XXXXX
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setPlayerImageBase64(base64);
      setPlayerImageFileName(file.name);
      setIsReadingPlayerImage(false);
    };
    reader.onerror = () => {
      setErrorMessage('Unable to read selected image file.');
      setIsReadingPlayerImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitState('submitting');

    try {
      if (isReadingPlayerImage) {
        setErrorMessage('Please wait for the image to finish loading.');
        setSubmitState('idle');
        return;
      }

      // Persist image validation errors and prevent submitting invalid images.
      if (errorMessage) {
        setSubmitState('idle');
        return;
      }

      const payload = {
        tableName: 'tigers_players',
        teamName: 'tigers',
        name: toTitleCase(name),
        position: normalizePosition(position),
        number: Number(number),
        shoots: shoots || undefined,
        sponsor: sponsorName || undefined,
        sponsorLink: sponsorUrl || undefined,
        sponsorImg: sponsorImageUrl || undefined,
        funfact: funFact || undefined,
        ...(playerImageBase64 !== null ? { imageBase64: playerImageBase64 } : {}),
      };

      const response = await fetch(addPlayerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': session.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to add player');
      }

      setSubmitState('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error while adding player';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  const resetForm = () => {
    setName('');
    setPosition('');
    setNumber('');
    setShoots('');
    setSponsorName('');
    setSponsorUrl('');
    setSponsorImageUrl('');
    setFunFact('');
    setPlayerImageBase64(null);
    setPlayerImageFileName(null);
    setIsReadingPlayerImage(false);
    setImageInputKey((k) => k + 1);
    setSubmitState('idle');
    setErrorMessage(null);
  };

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>Player Added</Title>
        <Message>The player was added successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Add another player
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>Player Not Added</Title>
        <ErrorMessage>
          There was a problem adding the player.
          {errorMessage ? <div style={{ marginTop: '0.5rem' }}>{errorMessage}</div> : null}
        </ErrorMessage>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Add a Player</Title>
      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label htmlFor="name">
            Name<RequiredMark>*</RequiredMark>
          </Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="position">
            Position<RequiredMark>*</RequiredMark>
          </Label>
          <Select
            id="position"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            required
          >
            <option value="">Select position</option>
            <option value="Forward">Forward</option>
            <option value="Defense">Defense</option>
            <option value="Forward/Defense">Forward/Defense</option>
            <option value="Goalie">Goalie</option>
          </Select>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="number">
            Number<RequiredMark>*</RequiredMark>
          </Label>
          <Input
            id="number"
            type="number"
            min="0"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="shoots">Shoots (L/R)</Label>
          <Select
            id="shoots"
            value={shoots}
            onChange={(e) => setShoots(e.target.value)}
          >
            <option value="">Not set</option>
            <option value="Left">L</option>
            <option value="Right">R</option>
          </Select>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="sponsorName">Sponsor Name</Label>
          <Input
            id="sponsorName"
            type="text"
            value={sponsorName}
            onChange={(e) => setSponsorName(e.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="sponsorUrl">Sponsor URL</Label>
          <Input
            id="sponsorUrl"
            type="url"
            value={sponsorUrl}
            onChange={(e) => setSponsorUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="sponsorImageUrl">Sponsor Image URL</Label>
          <Input
            id="sponsorImageUrl"
            type="url"
            value={sponsorImageUrl}
            onChange={(e) => setSponsorImageUrl(e.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="funFact">Fun Fact</Label>
          <Input
            id="funFact"
            type="text"
            value={funFact}
            onChange={(e) => setFunFact(e.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="playerImage">Player Image (PNG, max 400KB)</Label>
          <FileInput
            key={imageInputKey}
            id="playerImage"
            type="file"
            accept="image/png"
            onChange={(e) => handlePlayerImageFile(e.target.files?.[0] ?? null)}
          />
          {errorMessage ? <UploadError>{errorMessage}</UploadError> : null}
          {playerImageFileName ? (
            <HelperText>
              Selected: <span style={{ color: '#c9f7d6' }}>{playerImageFileName}</span>
            </HelperText>
          ) : null}
        </FieldGroup>

        <SubmitButton type="submit" disabled={submitState === 'submitting' || isReadingPlayerImage}>
          {isReadingPlayerImage
            ? 'Loading image…'
            : submitState === 'submitting'
              ? 'Submitting…'
              : 'Add Player'}
        </SubmitButton>
        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default AddPlayer;

